import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { X } from 'lucide-react';

const PLANET_RANKS = [
  { rank: 'mercure', fr: 'Mercure', en: 'Mercury', color: '#9CA3AF', members: 4 },
  { rank: 'venus', fr: 'Vénus', en: 'Venus', color: '#FBBF24', members: 16 },
  { rank: 'terre', fr: 'Terre', en: 'Earth', color: '#34D399', members: 64 },
  { rank: 'mars', fr: 'Mars', en: 'Mars', color: '#F87171', members: 256 },
  { rank: 'jupiter', fr: 'Jupiter', en: 'Jupiter', color: '#FB923C', members: 1024 },
  { rank: 'saturne', fr: 'Saturne', en: 'Saturn', color: '#A78BFA', members: 4096 },
  { rank: 'uranus', fr: 'Uranus', en: 'Uranus', color: '#67E8F9', members: 16384 },
];

const TOTAL_POTENTIAL = 21844;
const DIRECTIONS = [
  { key: 'N', label: 'Nord', angle: -Math.PI / 2, color: '#4ADE80' },
  { key: 'E', label: 'Est', angle: 0, color: '#60A5FA' },
  { key: 'S', label: 'Sud', angle: Math.PI / 2, color: '#FB923C' },
  { key: 'W', label: 'Ouest', angle: Math.PI, color: '#C084FC' },
];

const FullPotentialOverlay = ({ isOpen, onClose, language = 'fr' }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [phase, setPhase] = useState(0);
  const [counter, setCounter] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [showFinal, setShowFinal] = useState(false);
  const animRef = useRef(null);

  const t = useCallback((fr, en) => language === 'fr' ? fr : en, [language]);

  // Reset when opening
  useEffect(() => {
    if (isOpen) {
      setPhase(0);
      setCounter(0);
      setCurrentLevel(0);
      setShowFinal(false);
      const timer = setTimeout(() => setPhase(1), 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Main animation sequence
  useEffect(() => {
    if (!isOpen || phase === 0) return;

    if (phase === 1) {
      // Animate levels appearing one by one
      let level = 0;
      const interval = setInterval(() => {
        level++;
        setCurrentLevel(level);
        if (level >= 7) {
          clearInterval(interval);
          setTimeout(() => setPhase(2), 600);
        }
      }, 500);
      return () => clearInterval(interval);
    }

    if (phase === 2) {
      // Counter animation
      const duration = 2000;
      const start = performance.now();
      const animate = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCounter(Math.floor(eased * TOTAL_POTENTIAL));
        if (progress < 1) {
          animRef.current = requestAnimationFrame(animate);
        } else {
          setCounter(TOTAL_POTENTIAL);
          setTimeout(() => setShowFinal(true), 300);
        }
      };
      animRef.current = requestAnimationFrame(animate);
      return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    }
  }, [isOpen, phase]);

  // D3 Visualization
  useEffect(() => {
    if (!isOpen || phase < 1 || !svgRef.current || !containerRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const rect = containerRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    svg.attr('width', width).attr('height', height);

    const cx = width / 2;
    const cy = height / 2;
    const maxRadius = Math.min(width, height) * 0.42;
    const TILT = 0.55;

    const g = svg.append('g');

    // Glow filter
    const defs = svg.append('defs');
    const glowFilter = defs.append('filter').attr('id', 'fp-glow');
    glowFilter.append('feGaussianBlur').attr('stdDeviation', '4').attr('result', 'coloredBlur');
    const feMerge = glowFilter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Pulse filter
    const pulseFilter = defs.append('filter').attr('id', 'fp-pulse');
    pulseFilter.append('feGaussianBlur').attr('stdDeviation', '8').attr('result', 'blur');
    const pMerge = pulseFilter.append('feMerge');
    pMerge.append('feMergeNode').attr('in', 'blur');
    pMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Draw orbit rings for each level
    for (let level = 1; level <= 7; level++) {
      const radius = (level / 7) * maxRadius;
      const planet = PLANET_RANKS[level - 1];
      const isActive = level <= currentLevel;

      // Elliptical orbit ring
      const ring = g.append('ellipse')
        .attr('cx', cx).attr('cy', cy)
        .attr('rx', 0).attr('ry', 0)
        .attr('fill', 'none')
        .attr('stroke', isActive ? planet.color : '#1E293B')
        .attr('stroke-width', isActive ? 2 : 0.5)
        .attr('stroke-dasharray', isActive ? 'none' : '4,4')
        .attr('opacity', isActive ? 0.6 : 0.15);

      if (isActive) {
        ring.transition()
          .duration(600)
          .ease(d3.easeCubicOut)
          .attr('rx', radius)
          .attr('ry', radius * TILT);

        // Add glow ring
        g.append('ellipse')
          .attr('cx', cx).attr('cy', cy)
          .attr('rx', 0).attr('ry', 0)
          .attr('fill', 'none')
          .attr('stroke', planet.color)
          .attr('stroke-width', 4)
          .attr('opacity', 0)
          .attr('filter', 'url(#fp-glow)')
          .transition()
          .duration(600)
          .ease(d3.easeCubicOut)
          .attr('rx', radius)
          .attr('ry', radius * TILT)
          .attr('opacity', 0.2);

        // Place nodes on this orbit
        const nodeCount = Math.min(planet.members, level === 1 ? 4 : level === 2 ? 16 : level === 3 ? 24 : level === 4 ? 32 : level === 5 ? 40 : level === 6 ? 48 : 56);

        for (let i = 0; i < nodeCount; i++) {
          const angle = (i / nodeCount) * Math.PI * 2 - Math.PI / 2;
          const nx = cx + radius * Math.cos(angle);
          const ny = cy + radius * TILT * Math.sin(angle);
          const nodeSize = Math.max(2, 6 - level * 0.5);

          g.append('circle')
            .attr('cx', cx).attr('cy', cy)
            .attr('r', 0)
            .attr('fill', planet.color)
            .attr('opacity', 0)
            .attr('filter', level <= 3 ? 'url(#fp-glow)' : null)
            .transition()
            .delay(100 + i * (300 / nodeCount))
            .duration(500)
            .ease(d3.easeCubicOut)
            .attr('cx', nx).attr('cy', ny)
            .attr('r', nodeSize)
            .attr('opacity', 0.8);
        }

        // Planet label
        const labelAngle = Math.PI * 1.25;
        const labelX = cx + (radius + 18) * Math.cos(labelAngle);
        const labelY = cy + (radius + 18) * TILT * Math.sin(labelAngle);

        g.append('text')
          .attr('x', labelX).attr('y', labelY)
          .attr('text-anchor', 'end')
          .attr('fill', planet.color)
          .attr('font-size', '10px')
          .attr('font-weight', '600')
          .attr('opacity', 0)
          .text(language === 'fr' ? planet.fr : planet.en)
          .transition()
          .delay(300)
          .duration(400)
          .attr('opacity', 0.7);
      } else {
        ring.attr('rx', radius).attr('ry', radius * TILT);
      }
    }

    // Direction arrows
    DIRECTIONS.forEach(dir => {
      const arrowLen = maxRadius + 30;
      const ax = cx + arrowLen * Math.cos(dir.angle);
      const ay = cy + arrowLen * TILT * Math.sin(dir.angle);

      g.append('text')
        .attr('x', ax).attr('y', ay)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('fill', dir.color)
        .attr('font-size', '14px')
        .attr('font-weight', '800')
        .attr('opacity', phase >= 1 ? 0.8 : 0)
        .text(dir.key);
    });

    // Center node (YOU)
    g.append('circle')
      .attr('cx', cx).attr('cy', cy)
      .attr('r', 18)
      .attr('fill', '#E05D3A')
      .attr('filter', 'url(#fp-pulse)')
      .attr('opacity', 0.9);

    g.append('text')
      .attr('x', cx).attr('y', cy)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('fill', 'white')
      .attr('font-size', '10px')
      .attr('font-weight', '800')
      .text(language === 'fr' ? 'MOI' : 'ME');

  }, [isOpen, phase, currentLevel, language]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="full-potential-overlay">
      {/* Backdrop */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{
          background: 'radial-gradient(ellipse at center, #0a0a1a 0%, #000000 100%)',
          opacity: phase >= 1 ? 1 : 0
        }}
        onClick={onClose}
      />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 text-white/60 hover:text-white hover:bg-white/20 transition-all"
        data-testid="fp-close-btn"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Main content */}
      <div className="relative w-full h-full flex flex-col items-center justify-center p-4">
        {/* Title */}
        <div className={`text-center mb-2 transition-all duration-700 ${phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
            {t('Potentiel Maximum', 'Full Potential')}
          </h2>
          <p className="text-white/40 text-sm mt-1">
            {t('Matrice 4 directions × 7 niveaux', '4 directions × 7 levels matrix')}
          </p>
        </div>

        {/* SVG Container */}
        <div ref={containerRef} className="flex-1 w-full max-w-3xl relative" style={{ minHeight: 400 }}>
          <svg ref={svgRef} className="w-full h-full" />

          {/* Level indicators appearing with animation */}
          {phase >= 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
              {PLANET_RANKS.map((p, i) => (
                <div
                  key={p.rank}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full transition-all duration-500 ${
                    i < currentLevel ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                  }`}
                  style={{
                    background: `${p.color}15`,
                    border: `1px solid ${p.color}40`,
                    transitionDelay: `${i * 100}ms`
                  }}
                >
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                  <span className="text-[10px] font-semibold" style={{ color: p.color }}>
                    {p.members.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Counter + Final Stats */}
        <div className={`text-center transition-all duration-700 ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="relative inline-block">
            <span className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#E05D3A] via-[#FBBF24] to-[#A78BFA] tabular-nums">
              {counter.toLocaleString()}
            </span>
            {showFinal && (
              <span className="absolute -top-2 -right-14 text-sm font-bold text-emerald-400 animate-bounce">
                MAX
              </span>
            )}
          </div>
          <p className="text-white/40 text-sm mt-1">
            {t('membres dans votre réseau', 'members in your network')}
          </p>
        </div>

        {/* Revenue potential (appears last) */}
        {showFinal && (
          <div className="mt-4 grid grid-cols-3 gap-3 max-w-lg w-full animate-fade-in">
            {[
              { label: t('Matrice/mois', 'Matrix/month'), value: '$5,461', color: '#4ADE80' },
              { label: t('Fast Start', 'Fast Start'), value: '$109,220', color: '#FBBF24' },
              { label: t('Matching', 'Matching'), value: '$2,730/mo', color: '#A78BFA' },
            ].map((stat, i) => (
              <div
                key={i}
                className="text-center p-3 rounded-xl border animate-scale-in"
                style={{
                  background: `${stat.color}08`,
                  borderColor: `${stat.color}30`,
                  animationDelay: `${i * 150}ms`
                }}
              >
                <p className="text-lg md:text-xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                <p className="text-[10px] text-white/40 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scale-in { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
        .animate-fade-in { animation: fade-in 0.6s ease-out forwards; }
        .animate-scale-in { animation: scale-in 0.5s ease-out forwards; opacity: 0; }
      `}</style>
    </div>
  );
};

export default FullPotentialOverlay;
